/**
 * Sandpiper Management - PKCE OAuth 2.0 Authentication Service
 *
 * Supports per-environment Cognito configuration so that switching
 * API servers triggers re-authentication against that environment's
 * Cognito User Pool.
 *
 * Flow:
 *   1. Generate PKCE code_verifier + code_challenge
 *   2. Redirect to Cognito /oauth2/authorize
 *   3. User authenticates (Cognito native or SAML via IAM Identity Center)
 *   4. Cognito redirects back with authorization code
 *   5. Exchange code + verifier for tokens via /oauth2/token
 *   6. Access token used as Bearer token for API calls
 *   7. Refresh token used to silently renew before expiry
 */

import type { CognitoConfig } from './environments';

// ============================================================
// Types
// ============================================================

export interface AuthConfig extends CognitoConfig {
  _envKey: string | null;
}

export interface Tokens {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
}

export interface AuthInitResult {
  authenticated: boolean;
  needsLogin: boolean;
  environmentKey?: string | null;
}

export interface AuthEvent {
  type: 'token_refreshed' | 'logout';
  tokens?: Tokens;
}

type AuthCallback = (event: AuthEvent) => void;

// ============================================================
// PKCE Helpers
// ============================================================

const base64UrlEncode = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

const generateState = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

// ============================================================
// Token & Config Storage
// ============================================================

let activeConfig: AuthConfig | null = null;
let tokens: Tokens = { accessToken: null, idToken: null, refreshToken: null, expiresAt: null };
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const authListeners = new Set<AuthCallback>();

const STORAGE_KEY = 'sandpiper_auth';
const PKCE_VERIFIER_KEY = 'pkce_code_verifier';
const PKCE_STATE_KEY = 'pkce_state';
const AUTH_ENV_KEY = 'sandpiper_auth_env';

// Guard against concurrent callback handling (React StrictMode double-invokes effects)
let callbackInFlight: Promise<Tokens | null> | null = null;

const persistSession = (): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    if (activeConfig) {
      sessionStorage.setItem(AUTH_ENV_KEY, JSON.stringify(activeConfig));
    }
  } catch { /* sessionStorage unavailable */ }
};

const loadPersistedSession = (): boolean | 'needs_refresh' => {
  try {
    const storedConfig = sessionStorage.getItem(AUTH_ENV_KEY);
    const storedTokens = sessionStorage.getItem(STORAGE_KEY);
    if (!storedConfig || !storedTokens) return false;

    const parsedConfig: AuthConfig = JSON.parse(storedConfig);
    const parsedTokens: Tokens = JSON.parse(storedTokens);

    activeConfig = parsedConfig;

    if (parsedTokens.expiresAt && parsedTokens.expiresAt > Date.now()) {
      tokens = parsedTokens;
      scheduleRefresh();
      return true;
    }

    if (parsedTokens.refreshToken) {
      tokens = parsedTokens;
      return 'needs_refresh';
    }

    clearSession();
  } catch { /* ignore */ }
  return false;
};

const clearSession = (): void => {
  tokens = { accessToken: null, idToken: null, refreshToken: null, expiresAt: null };
  activeConfig = null;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(AUTH_ENV_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_STATE_KEY);
  } catch { /* ignore */ }
};

// ============================================================
// Token Refresh
// ============================================================

const scheduleRefresh = (): void => {
  if (refreshTimer) clearTimeout(refreshTimer);
  if (!tokens.refreshToken || !tokens.expiresAt || !activeConfig) return;

  const msUntilExpiry = tokens.expiresAt - Date.now();
  const refreshIn = Math.max(msUntilExpiry - 120_000, 10_000);

  refreshTimer = setTimeout(async () => {
    try {
      await refreshAccessToken();
    } catch (err) {
      console.warn('[Auth] Token refresh failed:', (err as Error).message);
    }
  }, refreshIn);
};

const refreshAccessToken = async (): Promise<Tokens> => {
  if (!tokens.refreshToken || !activeConfig) throw new Error('No refresh token or config');

  const res = await fetch(`${activeConfig.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: activeConfig.clientId,
      refresh_token: tokens.refreshToken,
    }),
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Token refresh failed: ${res.status} ${body}`);
  }

  const data = await res.json();
  tokens.accessToken = data.access_token;
  if (data.id_token) tokens.idToken = data.id_token;
  tokens.expiresAt = Date.now() + data.expires_in * 1000;

  persistSession();
  scheduleRefresh();
  authListeners.forEach((cb) => cb({ type: 'token_refreshed', tokens }));

  return tokens;
};

// ============================================================
// Redirect URI
// ============================================================

const getRedirectUri = (): string => `${window.location.origin}/callback`;

// ============================================================
// Public API
// ============================================================

export const auth = {
  /**
   * Get the currently active Cognito config (if authenticated).
   */
  getActiveConfig: (): AuthConfig | null => (activeConfig ? { ...activeConfig } : null),

  /**
   * Initialize — restore a persisted session or handle an OAuth callback.
   */
  init: async (): Promise<AuthInitResult> => {
    // 1. Check for callback authorization code
    const callbackResult = await auth.handleCallback();
    if (callbackResult) {
      return { authenticated: true, needsLogin: false, environmentKey: activeConfig?._envKey };
    }

    // 2. Restore persisted session
    const restored = loadPersistedSession();
    if (restored === true) {
      return { authenticated: true, needsLogin: false, environmentKey: activeConfig?._envKey };
    }
    if (restored === 'needs_refresh') {
      try {
        await refreshAccessToken();
        return { authenticated: true, needsLogin: false, environmentKey: activeConfig?._envKey };
      } catch {
        clearSession();
      }
    }

    return { authenticated: false, needsLogin: true };
  },

  /**
   * Start the login flow for a specific environment's Cognito config.
   */
  login: async (cognitoConfig: CognitoConfig, envKey?: string): Promise<void> => {
    if (!cognitoConfig?.domain || !cognitoConfig?.clientId) {
      throw new Error('Cognito domain and clientId are required');
    }

    const config: AuthConfig = {
      domain: cognitoConfig.domain.replace(/\/+$/, ''),
      clientId: cognitoConfig.clientId,
      scopes: cognitoConfig.scopes || 'openid email profile',
      identityProvider: cognitoConfig.identityProvider || '',
      _envKey: envKey || null,
    };

    activeConfig = config;
    try {
      sessionStorage.setItem(AUTH_ENV_KEY, JSON.stringify(config));
    } catch { /* ignore */ }

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(PKCE_STATE_KEY, state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.clientId,
      redirect_uri: getRedirectUri(),
      scope: config.scopes,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    if (config.identityProvider) {
      params.set('identity_provider', config.identityProvider);
    }

    window.location.href = `${config.domain}/oauth2/authorize?${params}`;
  },

  /**
   * Handle the OAuth callback. Uses the persisted config to know
   * which Cognito token endpoint to call.
   * Deduplicates concurrent calls (e.g. React StrictMode double-invoke).
   */
  handleCallback: async (): Promise<Tokens | null> => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');
    const errorDescription = url.searchParams.get('error_description');

    if (error) {
      window.history.replaceState({}, '', url.pathname);
      throw new Error(`OAuth error: ${error} — ${errorDescription || 'Unknown error'}`);
    }

    if (!code) return null;

    // If a callback exchange is already in flight, return the same promise
    if (callbackInFlight) return callbackInFlight;

    callbackInFlight = (async () => {

    // Restore the config that was active when we started the login
    if (!activeConfig) {
      try {
        const stored = sessionStorage.getItem(AUTH_ENV_KEY);
        if (stored) activeConfig = JSON.parse(stored);
      } catch { /* ignore */ }
    }

    if (!activeConfig) {
      window.history.replaceState({}, '', url.pathname);
      throw new Error('No Cognito configuration found for callback — login flow was interrupted');
    }

    const savedState = sessionStorage.getItem(PKCE_STATE_KEY);
    if (state !== savedState) {
      window.history.replaceState({}, '', url.pathname);
      throw new Error('OAuth state mismatch — possible CSRF attack');
    }

    const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!codeVerifier) {
      window.history.replaceState({}, '', url.pathname);
      throw new Error('Missing PKCE code verifier — login flow was interrupted');
    }

    const res = await fetch(`${activeConfig.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: activeConfig.clientId,
        code,
        redirect_uri: getRedirectUri(),
        code_verifier: codeVerifier,
      }),
    });

    if (!res.ok) {
      const body = await res.text();
      window.history.replaceState({}, '', url.pathname);
      throw new Error(`Token exchange failed: ${res.status} ${body}`);
    }

    const data = await res.json();
    tokens.accessToken = data.access_token;
    tokens.idToken = data.id_token || null;
    tokens.refreshToken = data.refresh_token || null;
    tokens.expiresAt = Date.now() + data.expires_in * 1000;

    persistSession();
    scheduleRefresh();

    window.history.replaceState({}, '', url.pathname);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_STATE_KEY);

    return tokens;
    })().finally(() => { callbackInFlight = null; });

    return callbackInFlight;
  },

  /**
   * Logout — clear session and optionally redirect to Cognito logout.
   */
  logout: ({ redirect = true }: { redirect?: boolean } = {}): void => {
    const config = activeConfig;
    clearSession();
    authListeners.forEach((cb) => cb({ type: 'logout' }));

    if (redirect && config?.domain) {
      const params = new URLSearchParams({
        client_id: config.clientId,
        logout_uri: window.location.origin,
      });
      window.location.href = `${config.domain}/logout?${params}`;
    }
  },

  getAccessToken: (): string | null => {
    if (!tokens.accessToken) return null;
    if (tokens.expiresAt && tokens.expiresAt < Date.now()) return null;
    return tokens.accessToken;
  },

  getUserInfo: (): Record<string, unknown> | null => {
    if (!tokens.idToken) return null;
    try {
      const payload = tokens.idToken.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  },

  isAuthenticated: (): boolean =>
    Boolean(tokens.accessToken && tokens.expiresAt && tokens.expiresAt > Date.now()),

  onAuthChange: (callback: AuthCallback): (() => void) => {
    authListeners.add(callback);
    return () => { authListeners.delete(callback); };
  },

  refresh: refreshAccessToken,
};
